import { useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * CircuitNet — fondo "cíborg": red de puntos que se mueven y se unen con líneas cuando están cerca
 * (circuito tech). Port a React del canvas del mockup aprobado. Colores por día/noche, respeta
 * prefers-reduced-motion (no anima), limpia el rAF al desmontar y re-inicia al cambiar de tema o tamaño.
 */
export default function CircuitNet({ className = '' }) {
  const ref = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const ctx = c.getContext('2d');
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const dark = theme !== 'light';
    const LINE = dark ? 'rgba(124,160,220,.28)' : 'rgba(28,63,116,.34)';
    const DOT = dark ? 'rgba(124,194,62,.85)' : 'rgba(92,154,39,.80)';
    let w = 0, h = 0, pts = [], raf, rt;

    const size = () => { w = c.clientWidth; h = c.clientHeight; c.width = w * DPR; c.height = h * DPR; ctx.setTransform(DPR, 0, 0, DPR, 0, 0); };
    const init = () => {
      size();
      const N = Math.max(22, Math.min(58, Math.round((w * h) / 20000)));
      pts = [];
      for (let i = 0; i < N; i++) pts.push({ x: Math.random() * w, y: Math.random() * h, vx: (Math.random() - 0.5) * 0.28, vy: (Math.random() - 0.5) * 0.28 });
    };
    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) { p.x += p.vx; p.y += p.vy; if (p.x < 0 || p.x > w) p.vx *= -1; if (p.y < 0 || p.y > h) p.vy *= -1; }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i], b = pts[j], dx = a.x - b.x, dy = a.y - b.y, d = Math.sqrt(dx * dx + dy * dy);
          if (d < 125) { ctx.globalAlpha = 1 - d / 125; ctx.strokeStyle = LINE; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
        }
      }
      ctx.globalAlpha = 1; ctx.fillStyle = DOT;
      for (const p of pts) { ctx.beginPath(); ctx.arc(p.x, p.y, 1.8, 0, 6.283); ctx.fill(); }
    };

    let running = false;
    const loop = () => { if (!running) return; frame(); raf = requestAnimationFrame(loop); };
    const start = () => { if (!running) { running = true; loop(); } };
    const stop = () => { running = false; cancelAnimationFrame(raf); };

    init();
    // CWV: solo anima cuando el hero está a la vista (ahorra CPU al hacer scroll)
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? start() : stop()));
    }, { threshold: 0 });
    io.observe(c);

    const onResize = () => { clearTimeout(rt); rt = setTimeout(() => { stop(); init(); start(); }, 200); };
    window.addEventListener('resize', onResize);
    return () => { stop(); io.disconnect(); clearTimeout(rt); window.removeEventListener('resize', onResize); };
  }, [theme]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
